import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { submitPodcastQuestion, TURNSTILE_REQUIRED, extractApiError } from "@/lib/api";
import { ASK_GUEST_EVENT, pendingGuest } from "@/components/atfq/askGuest";

const initialForm = {
  question: "",
  topic: "Politics",
  submitter_name: "",
  submitter_email: "",
  is_public_allowed: false,
};

const turnstileMessage = (code) => {
  if (code.startsWith("1102")) {
    return `The security check isn't enabled for ${window.location.hostname} yet (Cloudflare error ${code}). The site owner needs to add this domain to the Turnstile widget.`;
  }
  if (code) return `The security check couldn't load (Cloudflare error ${code}). Please refresh and try again.`;
  return "Please complete the security check below.";
};

const useGuestPrefill = (setGuest, resetStatus) => {
  useEffect(() => {
    const onAsk = (e) => {
      setGuest(e.detail);
      resetStatus();
    };
    window.addEventListener(ASK_GUEST_EVENT, onAsk);
    const fromUrl = new URLSearchParams(window.location.search).get("guest");
    const pending = pendingGuest();
    if (pending) setGuest(pending);
    else if (fromUrl) setGuest({ name: fromUrl });
    return () => window.removeEventListener(ASK_GUEST_EVENT, onAsk);
  }, [setGuest, resetStatus]);
};

export const useAskForm = () => {
  const [form, setForm] = useState(initialForm);
  const [guest, setGuest] = useState(null);
  const [token, setToken] = useState("");
  const [tsError, setTsError] = useState("");
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const resetStatus = useCallback(() => setStatus({ state: "idle", message: "" }), []);
  useGuestPrefill(setGuest, resetStatus);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_REQUIRED && !token) {
      setStatus({ state: "error", message: turnstileMessage(tsError) });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await submitPodcastQuestion({
        question: form.question,
        topic: form.topic,
        guest: guest?.name || null,
        submitter_name: form.submitter_name || null,
        submitter_email: form.submitter_email || null,
        is_public_allowed: form.is_public_allowed,
        turnstile_token: token || null,
      });
      setStatus({ state: "success", message: "Question received — if it makes air, we'll answer it, fucking directly." });
      setForm(initialForm);
      setGuest(null);
      setToken("");
      toast.success("Question submitted to ATFQ", { description: "Thanks for keeping the mic honest." });
    } catch (err) {
      setStatus({ state: "error", message: extractApiError(err) });
    }
  };

  const turnstile = {
    onToken: (t) => {
      setToken(t);
      if (t) setTsError("");
    },
    onError: (code) => {
      setToken("");
      setTsError(code || "unknown");
    },
  };

  return { form, guest, setGuest, status, setField, toggleField, onSubmit, turnstile };
};
